import React from 'react';
import { Form, Counter, Card, Message, ToggleContainer, Section, TooltipHelp, Select } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import Style from './cartCard.module.css';
import PropTypes from 'prop-types';

function BundleAndPreselectedQuantityForm({ form, onChange, onSetForm, errors, event, toggleBundleSale, setToggleBundleSale, onChangeCounter }) {
    const { t } = useTranslation();
    const onToggleBundleSale = React.useCallback(
        checked => {
            if (!checked) {
                onSetForm({ bundleSize: 1 });
            }
            setToggleBundleSale(!toggleBundleSale);
        },
        [setToggleBundleSale, toggleBundleSale, onSetForm]
    );

    const selectOptions = React.useMemo(() => {
        const start = form.minQuantity || 0;
        const end = form.maxQuantity || 15;
        const step = form.bundleSize || 1;
        let options = [];
        if (form.minQuantity) {
            options.push({ text: '0', value: 0, key: 0 });
        }
        for (let iterator = start; iterator <= end; iterator += step) {
            options.push({ text: iterator.toString(), value: iterator, key: iterator });
        }

        return options;
    }, [form]);

    return (
        <div>
            <Form.Field>
                <Form.Label>
                    {t('ticket.rates.optionalSettings.cart.defaultQuantity')}
                    <TooltipHelp title={t('ticket.rates.optionalSettings.cart.defaultQuantityTooltip')} />
                </Form.Label>
                <div className={Style['select-input']}>
                    <Select
                        onChange={value => {
                            onChange('defaultQuantity', value);
                        }}
                        options={selectOptions}
                        value={form.defaultQuantity || 0}
                    />
                </div>
            </Form.Field>

            <ToggleContainer
                toggle={toggleBundleSale}
                onToggle={onToggleBundleSale}
                title={
                    <span>
                        {t('ticket.rates.optionalSettings.cart.toggleBundleSale')}
                        <TooltipHelp position={'top'} title={t('ticket.rates.optionalSettings.cart.toggleBundleSaleTooltip')} />
                    </span>
                }
            >
                <Section className={Style['cart-card-section']}>
                    <Form.Field>
                        <Form.Label>{t('ticket.rates.optionalSettings.cart.bundleSize')}</Form.Label>
                        <Counter
                            errors={errors['bundleSize'] ? true : false}
                            style={{ width: '122px' }}
                            placeholder={0}
                            onChange={(event, value) => onChangeCounter('bundleSize', value, 1)}
                            value={form.bundleSize || 1}
                            min={1}
                        />
                        <Form.FieldErrors errors={[errors['bundleSize'] || null]} />
                    </Form.Field>
                </Section>
            </ToggleContainer>
        </div>
    );
}

BundleAndPreselectedQuantityForm.propTypes = {
    toggleBundleSale: PropTypes.bool.isRequired,
    setToggleBundleSale: PropTypes.func.isRequired,
    onChangeCounter: PropTypes.func.isRequired,
    form: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onSetForm: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired
};

export default function CartCard({ form, onChange, onSetForm, errors, rate, event }) {
    const { t } = useTranslation();
    const [toggleQuantity, setToggleQuantity] = React.useState(false);

    const [toggleBundleSale, setToggleBundleSale] = React.useState(false);

    React.useEffect(() => {
        setToggleQuantity(form.minQuantity === null && form.maxQuantity === null ? false : true);
        setToggleBundleSale(form.bundleSize !== 1);
    }, []);

    const onToggleQuantity = React.useCallback(
        checked => {
            if (!checked) {
                onSetForm({ minQuantity: null, maxQuantity: null });
            }
            setToggleQuantity(!toggleQuantity);
        },
        [onSetForm, setToggleQuantity, toggleQuantity]
    );

    const onChangeCounter = React.useCallback(
        (field, value, defaultValue = 0) => {
            let data = {};
            if (value === 0) {
                value = defaultValue;
            }
            data[field] = value;
            data['defaultQuantity'] = null;
            onSetForm(data);
        },
        [onChange]
    );

    return (
        <Card.FormStep
            title={t(`ticket.rates.optionalSettings.cart.title`)}
            subtitle={t(`ticket.rates.optionalSettings.cart.subtitle`)}
            rightComponent={
                <Message type="tips" title={t('ticket.common.tips')} mini>
                    {t('ticket.rates.optionalSettings.cart.tips')}
                </Message>
            }
        >
            <Form>
                <ToggleContainer toggle={toggleQuantity} onToggle={onToggleQuantity} title={t('ticket.rates.optionalSettings.cart.toggleQuantity')}>
                    <Section className={Style['cart-card-section']}>
                        <div className={Style['quantity-container']}>
                            <Form.Field className={Style['min-quantity-container']}>
                                <Form.Label>{t('ticket.rates.optionalSettings.cart.minQuantity')}</Form.Label>
                                <Counter
                                    errors={errors['minQuantity'] ? true : false}
                                    style={{ width: '150px' }}
                                    placeholder={0}
                                    onChange={(event, value) => onChangeCounter('minQuantity', value, null)}
                                    value={form.minQuantity}
                                    min={0}
                                />
                            </Form.Field>
                            <Form.Field>
                                <Form.Label>{t('ticket.rates.optionalSettings.cart.maxQuantity')}</Form.Label>
                                <Counter
                                    errors={errors['maxQuantity'] ? true : false}
                                    style={{ width: '150px' }}
                                    placeholder={0}
                                    onChange={(event, value) => onChangeCounter('maxQuantity', value, null)}
                                    value={form.maxQuantity}
                                    min={0}
                                />
                            </Form.Field>
                        </div>
                        <Form.FieldErrors errors={[errors['minQuantity'] || null]} />
                    </Section>
                </ToggleContainer>

                <BundleAndPreselectedQuantityForm
                    form={form}
                    onSetForm={onSetForm}
                    onChange={onChange}
                    errors={errors}
                    event={event}
                    toggleBundleSale={toggleBundleSale}
                    setToggleBundleSale={setToggleBundleSale}
                    onChangeCounter={onChangeCounter}
                />
            </Form>
        </Card.FormStep>
    );
}

CartCard.propTypes = {
    form: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onSetForm: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    rate: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired
};
